<template>
  <el-config-provider :locale="elementLocale" :button="buttonConfig" :size="uiStore.componentSize">
    <router-view v-if="startupState === 'ready'" />
    <div v-else class="app-startup" role="status" aria-live="polite">
      <span v-if="startupState !== 'error'" class="app-startup__spinner" aria-hidden="true"></span>
      <strong>{{ startupTitle }}</strong>
      <p v-if="startupState === 'slow'">正在校验登录状态，请稍候...</p>
      <button v-if="startupState === 'error'" type="button" @click="reloadApp">重新加载</button>
    </div>
  </el-config-provider>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import en from 'element-plus/es/locale/lang/en'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { useUiStore } from '@/stores/ui'
import { reloadApplication } from '@/utils/applicationReload'

type StartupState = 'loading' | 'slow' | 'ready' | 'error'

const router = useRouter()
const uiStore = useUiStore()
const buttonConfig = reactive({ autoInsertSpace: false })
const startupState = ref<StartupState>('loading')
const elementLocale = computed(() => (uiStore.language === 'zh-CN' ? zhCn : en))
const startupTitle = computed(() => (startupState.value === 'error' ? '页面初始化失败' : '正在加载管理后台'))
const slowTimer = window.setTimeout(() => {
  if (startupState.value === 'loading') startupState.value = 'slow'
}, 3000)

router
  .isReady()
  .then(() => {
    startupState.value = 'ready'
  })
  .catch(() => {
    startupState.value = 'error'
  })
  .finally(() => window.clearTimeout(slowTimer))

onBeforeUnmount(() => window.clearTimeout(slowTimer))

function reloadApp() {
  reloadApplication(router.currentRoute.value.fullPath)
}
</script>

<style scoped>
.app-startup {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  color: var(--el-text-color-regular);
  background: var(--admin-page);
}

.app-startup__spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--el-border-color);
  border-top-color: var(--el-color-primary);
  border-radius: 50%;
  animation: app-startup-spin 0.8s linear infinite;
}

.app-startup strong {
  color: var(--el-text-color-primary);
}

.app-startup p {
  margin: 0;
  color: var(--el-text-color-secondary);
}

.app-startup button {
  min-height: 34px;
  padding: 0 16px;
  color: #fff;
  cursor: pointer;
  background: var(--el-color-primary);
  border: 0;
  border-radius: 4px;
}

@keyframes app-startup-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
